import React from 'react';
import { Alert, Button, Col, FloatingLabel, Form, Row, Spinner } from 'react-bootstrap';
import { allProductPostingGroups, ledgerCodesByUser } from '@src/Api';
import type { DynamicsBcSettingsModuleProps } from '@src/pages/settings/types';
import type { LedgerCode } from './types';

interface PostingGroup {
  id: string;
  code: string;
  description: string;
}
type Props = DynamicsBcSettingsModuleProps & { onLedgerCodesLoaded: (ledgers: LedgerCode[]) => void };

export const LedgerPostingGroups: React.FC<Props> = ({ dynamicsBcUserDefaultData, setDynamicsBcUserDefaultData, onLedgerCodesLoaded }) => {
  const [ledgers, setLedgers] = React.useState<LedgerCode[]>([]);
  const [groups, setGroups] = React.useState<PostingGroup[]>([]);
  const [scope, setScope] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [reload, setReload] = React.useState(0);
  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const load = async () => {
      try {
        const [ledgerResponse, groupResponse] = await Promise.all([ledgerCodesByUser(), allProductPostingGroups()]);
        if (!active) return;
        if (
          ledgerResponse?.status !== 200 ||
          !Array.isArray(ledgerResponse.data) ||
          !ledgerResponse.data.every(
            (l: LedgerCode) => Number.isSafeInteger(l.id) && l.id > 0 && typeof l.displayname === 'string' && typeof l.code === 'string',
          )
        ) {
          throw new Error('Unable to load Rentman ledgers. Check the Rentman connection and retry.');
        }
        setLedgers(ledgerResponse.data);
        onLedgerCodesLoaded(ledgerResponse.data);
        if (
          groupResponse?.status !== 200 ||
          !Array.isArray(groupResponse.data?.groups) ||
          typeof groupResponse.data?.scope !== 'string' ||
          !groupResponse.data.groups.every((g: PostingGroup) => typeof g.id === 'string' && typeof g.code === 'string' && typeof g.description === 'string')
        ) {
          throw new Error('Unable to load Business Central product posting groups. Check the selected company and application permissions, then retry.');
        }
        setGroups(groupResponse.data.groups);
        setScope(groupResponse.data.scope);
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : 'Unable to load ledger mapping choices.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [reload, onLedgerCodesLoaded]);

  const mapping = dynamicsBcUserDefaultData.product_posting_group_mapping;
  const differentScope = !!mapping && !!scope && mapping.scope !== scope;
  const disabled = loading || !!error || differentScope || !setDynamicsBcUserDefaultData;
  const enableMapping = () =>
    setDynamicsBcUserDefaultData?.((previous) => ({
      ...previous,
      product_posting_group_mapping: { scope, ledgers: {} },
    }));
  const changeGroup = (id: string, code: string) =>
    setDynamicsBcUserDefaultData?.((previous) => {
      const current = previous.product_posting_group_mapping;
      if (!current || current.scope !== scope) return previous;
      const next = { ...current, ledgers: { ...current.ledgers } };
      if (id === 'rounding') {
        if (code) next.rounding = code;
        else delete next.rounding;
      } else if (code) next.ledgers[id] = code;
      else delete next.ledgers[id];
      return { ...previous, product_posting_group_mapping: next };
    });
  const options = (selected: string) => (
    <>
      <option value="">Select a product posting group</option>
      {selected && !groups.some((g) => g.code === selected) && <option value={selected}>{selected} (unavailable in selected company)</option>}
      {groups.map((group) => (
        <option key={group.id} value={group.code}>
          {group.code}
          {group.description ? ` — ${group.description}` : ''}
        </option>
      ))}
    </>
  );
  return (
    <>
      <Form.Group as={Row} className="mb-3" controlId="enable-ledger-posting-groups">
        <Form.Label column sm={6}>
          Use product posting groups by ledger
        </Form.Label>
        <Col sm={6}>
          <Form.Check
            type="switch"
            label="Enable"
            aria-describedby="ledger-posting-groups-description"
            checked={!!mapping}
            disabled={loading || !!error || !setDynamicsBcUserDefaultData}
            onChange={(event) => {
              if (event.target.checked) enableMapping();
              else
                setDynamicsBcUserDefaultData?.((previous) => {
                  const next = { ...previous };
                  delete next.product_posting_group_mapping;
                  return next;
                });
            }}
          />
          <Form.Text id="ledger-posting-groups-description" className="d-block mt-2" muted>
            {mapping
              ? 'Choose a product posting group for each ledger used on invoices. Existing items must already use the selected group.'
              : 'Until enabled, new items use the existing SERVICES group.'}
          </Form.Text>
          {loading && (
            <Form.Text className="d-block mt-2" role="status" muted>
              <Spinner as="span" animation="border" size="sm" /> Loading ledgers and product posting groups…
            </Form.Text>
          )}
          {error && (
            <Alert variant="danger" className="mt-2 mb-0">
              {error}{' '}
              <Button size="sm" variant="outline-danger" onClick={() => setReload((value) => value + 1)}>
                Retry
              </Button>
            </Alert>
          )}
          {differentScope && (
            <Alert variant="warning" className="mt-2 mb-0">
              The saved mapping belongs to a different Business Central company or environment.{' '}
              <Button size="sm" variant="outline-dark" onClick={enableMapping}>
                Start a mapping for this connection
              </Button>
            </Alert>
          )}
          {mapping && !loading && !error && groups.length === 0 && (
            <Alert variant="warning" className="mt-2 mb-0">
              No product posting groups were found in the selected company.
            </Alert>
          )}
        </Col>
      </Form.Group>
      {mapping && (
        <>
          {ledgers.map((ledger) => (
            <Form.Group as={Row} className="mb-3" controlId={`ledger-posting-group-${ledger.id}`} key={ledger.id}>
              <Form.Label column sm={6}>
                {ledger.displayname}
                {ledger.code !== ledger.displayname ? ` (${ledger.code || 'no code'})` : ''}
              </Form.Label>
              <Col sm={6}>
                <FloatingLabel controlId={`ledger-posting-group-${ledger.id}`} label="Select product posting group">
                  <Form.Select
                    value={mapping.ledgers[String(ledger.id)] || ''}
                    disabled={disabled}
                    isInvalid={!!mapping.ledgers[String(ledger.id)] && !loading && !error && !groups.some((g) => g.code === mapping.ledgers[String(ledger.id)])}
                    onChange={(event) => changeGroup(String(ledger.id), event.target.value)}
                  >
                    {options(mapping.ledgers[String(ledger.id)] || '')}
                  </Form.Select>
                </FloatingLabel>
              </Col>
            </Form.Group>
          ))}
          <Form.Group as={Row} className="mb-3" controlId="rounding-posting-group">
            <Form.Label column sm={6}>
              Rounding item
            </Form.Label>
            <Col sm={6}>
              <FloatingLabel controlId="rounding-posting-group" label="Select product posting group">
                <Form.Select
                  value={mapping.rounding || ''}
                  disabled={disabled}
                  isInvalid={!!mapping.rounding && !loading && !error && !groups.some((g) => g.code === mapping.rounding)}
                  onChange={(event) => changeGroup('rounding', event.target.value)}
                >
                  {options(mapping.rounding || '')}
                </Form.Select>
              </FloatingLabel>
              <Form.Text className="d-block mt-2" muted>
                Select a group if a rounding item is needed. Invoices stop if a required mapping is missing.
              </Form.Text>
            </Col>
          </Form.Group>
        </>
      )}
    </>
  );
};
